import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  List,
  ListItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  FilterList as FilterListIcon,
  GetApp as GetAppIcon,
  PersonAdd as PersonAddIcon,
  MoreVert as MoreVertIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { ref, update, remove, get } from 'firebase/database';
import { database } from '../../../firebase';
import { ecommerceApi, accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import GuideButton from '../../common/guide/GuideButton';
import { Autocomplete, Snackbar } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslation } from 'react-i18next';

// Üye veri yapısı
interface Member {
  id: string;
  uid: string; // Firebase Auth UID
  email: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatar?: string;
  isActive: boolean;
  isBlocked: boolean;
  createdAt: string;
  lastLoginAt?: string;
  orderCount: number;
  totalSpent: number;
  cartItemCount: number;
  addresses?: Array<{
    id: string;
    title: string;
    fullName: string;
    address: string;
    city: string;
    district: string;
    postalCode: string;
    isDefault: boolean;
  }>;
  preferences?: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    language: string;
  };
  sellerApplicationStatus?: 'none' | 'pending' | 'approved' | 'rejected';
  sellerApplicationDate?: string;
  // ===== B2B / cari eşleştirme (Faz 5.3) =====
  username?: string;
  authProvider?: string;
  isB2B?: boolean;
  isB2BApproved?: boolean;
  accountId?: number | null;
  accountName?: string | null;
}

interface MembersManagementProps {
  companyId: string | null;
}

const MembersManagement: React.FC<MembersManagementProps> = ({ companyId }) => {
  const { t } = useTranslation('salesSystem');
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'blocked'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [currentMemberMenu, setCurrentMemberMenu] = useState<Member | null>(null);
  const [cartModalOpen, setCartModalOpen] = useState(false);
  const [memberCart, setMemberCart] = useState<any[]>([]);
  const [loadingCart, setLoadingCart] = useState(false);
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [shippingCompany, setShippingCompany] = useState('');
  const [orderToShip, setOrderToShip] = useState<any>(null);
  const [partnerLoadWarning, setPartnerLoadWarning] = useState<string | null>(null);

  // ===== B2B üye→cari eşleştirme (Faz 5.3) =====
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignMember, setAssignMember] = useState<Member | null>(null);
  const [assignAccounts, setAssignAccounts] = useState<any[]>([]);
  const [assignSelectedAccount, setAssignSelectedAccount] = useState<any | null>(null);
  const [assignIsB2B, setAssignIsB2B] = useState(false);
  const [assignIsB2BApproved, setAssignIsB2BApproved] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignAccountsLoading, setAssignAccountsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // İstatistikler
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeMembers: 0,
    blockedMembers: 0,
    newMembersThisMonth: 0,
    totalOrders: 0,
    totalRevenue: 0
  });

  // Üyeleri SQL'den yükle
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const loadMembers = async () => {
      console.log('👥 [MEMBERS MANAGEMENT] SQL\'den üye verilerini yüklüyor...');
      
      try {
        const [membersResponse, partnerResponse] = await Promise.all([
          ecommerceApi.get('/api/ecommerce/members'),
          ecommerceApi.get('/api/ecommerce/partner', { params: { page: 1, pageSize: 500 } }).catch(() => null),
        ]);
        const data = membersResponse.data;
        
        if (!data.success) {
          throw new Error(data.message || t('ecommerceMembers.errors.membersLoadFailed'));
        }

        const membersData = data.data || [];
        const partnerData = partnerResponse?.data?.data || [];

        if (!partnerResponse) {
          setPartnerLoadWarning(t('ecommerceMembers.warnings.partnerLoadWarning'));
        } else {
          setPartnerLoadWarning(null);
        }

        const normalizeStatus = (status?: string): Member['sellerApplicationStatus'] => {
          const raw = String(status || '').trim().toLowerCase();
          if (raw === 'pending') return 'pending';
          if (raw === 'approved') return 'approved';
          if (raw === 'rejected') return 'rejected';
          return 'none';
        };

        const latestPartnerByKey = new Map<string, any>();
        partnerData.forEach((item: any) => {
          const keys = [
            String(item.memberId || '').toLowerCase(),
            String(item.memberFirebaseUid || '').toLowerCase(),
            String(item.memberEmail || '').toLowerCase(),
            String(item.email || '').toLowerCase()
          ].filter(Boolean);
          keys.forEach((key) => {
            const existing = latestPartnerByKey.get(key);
            const currentTime = new Date(item.createdAt || 0).getTime();
            const existingTime = new Date(existing?.createdAt || 0).getTime();
            if (!existing || currentTime >= existingTime) {
              latestPartnerByKey.set(key, item);
            }
          });
        });
        
        if (membersData.length === 0) {
          console.log('📭 [MEMBERS MANAGEMENT] Hiç üye bulunamadı');
          setMembers([]);
          updateStats([]);
          setLoading(false);
          return;
        }

        // SQL'den gelen veriyi Member formatına dönüştür
        const membersList: Member[] = membersData.map((member: any) => ({
          ...(function() {
            const memberIdKey = String(member.id || '').toLowerCase();
            const uidKey = String(member.uid || '').toLowerCase();
            const emailKey = String(member.email || '').toLowerCase();
            const matchedPartner =
              latestPartnerByKey.get(memberIdKey) ||
              latestPartnerByKey.get(uidKey) ||
              latestPartnerByKey.get(emailKey);
            return {
              sellerApplicationStatus: matchedPartner ? normalizeStatus(matchedPartner.status) : 'none',
              sellerApplicationDate: matchedPartner?.createdAt
            };
          })(),
          id: member.id,
          uid: member.uid || member.id, // Firebase UID veya SQL ID
          email: member.email || '',
          displayName: member.displayName || `${member.firstName || ''} ${member.lastName || ''}`.trim(),
          firstName: member.firstName || '',
          lastName: member.lastName || '',
          phone: member.phone || '',
          avatar: member.avatar || '',
          isActive: member.isActive !== false,
          isBlocked: member.isBlocked === true,
          createdAt: member.createdAt || new Date().toISOString(),
          lastLoginAt: member.lastLoginAt,
          orderCount: member.orderCount || 0,
          totalSpent: member.totalSpent || 0,
          cartItemCount: 0, // Sepet bilgisi ayrı endpoint'ten alınacak
          addresses: [], // Adresler ayrı endpoint'ten alınacak
          preferences: {
            emailNotifications: member.emailNotifications !== false,
            smsNotifications: member.smsNotifications === true,
            language: 'tr'
          },
          username: member.username || '',
          authProvider: member.authProvider || 'firebase',
          isB2B: member.isB2B === true,
          isB2BApproved: member.isB2BApproved === true,
          accountId: member.accountId ?? null,
          accountName: member.accountName ?? null
        }));

        membersList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

        console.log('✅ [MEMBERS MANAGEMENT] SQL\'den üye listesi yüklendi:', membersList.length, 'üye');
        
        setMembers(membersList);
        updateStats(membersList);
      } catch (error) {
        console.error('❌ [MEMBERS MANAGEMENT] SQL\'den üyeler yüklenirken hata:', error);
        setError(t('ecommerceMembers.errors.membersLoadFailed'));
      } finally {
        setLoading(false);
      }
    };
    
    loadMembers();
  }, [companyId]);

  // İstatistikleri güncelle
  const updateStats = (membersList: Member[]) => {
    const totalMembers = membersList.length;
    const activeMembers = membersList.filter(m => m.isActive && !m.isBlocked).length;
    const blockedMembers = membersList.filter(m => m.isBlocked).length;
    
    // Bu ay kayıt olan üyeler
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const newMembersThisMonth = membersList.filter(m => {
      const createdDate = new Date(m.createdAt);
      return createdDate.getMonth() === currentMonth && createdDate.getFullYear() === currentYear;
    }).length;

    const totalOrders = membersList.reduce((sum, m) => sum + m.orderCount, 0);
    const totalRevenue = membersList.reduce((sum, m) => sum + m.totalSpent, 0);

    setStats({
      totalMembers,
      activeMembers,
      blockedMembers,
      newMembersThisMonth,
      totalOrders,
      totalRevenue
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, member: Member) => {
    setMenuAnchorEl(event.currentTarget);
    setCurrentMemberMenu(member);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    // We don't nullify currentMemberMenu here to allow menu items to access it
  };



  // Üye sepetini görüntüle
  const handleViewCart = async (member: Member) => {
    if (!companyId) return;

    setLoadingCart(true);
    setCartModalOpen(true);
    setSelectedMember(member);
    
    try {
      const cartRef = ref(database, `companies/${companyId}/ecommerce/members/${member.id}/cart`);
      const snapshot = await get(cartRef);

      if (snapshot.exists()) {
        const cartData = snapshot.val();
        
        // Düzeltme: Object.entries kullanarak productId'yi (anahtar) verinin içine ekliyoruz.
        const cartItems: any[] = Object.entries(cartData).map(([productId, data]: [string, any]) => ({
          productId,
          ...data
        }));

        const enrichedCartPromises = cartItems.map(async (item) => {
          if (!item.productId) {
              return { ...item, name: item.name || t('ecommerceMembers.cart.missingProductInfoNoId'), image: item.image || '', price: 0 };
          }
          
          try {
            const productRefPath = `companies/${companyId}/inventory/inventoryItems/${item.productId}`;
            const productRef = ref(database, productRefPath);
            const productSnapshot = await get(productRef);

            if (productSnapshot.exists()) {
              const productData = productSnapshot.val();
              return {
                ...item,
                name: productData.name || t('ecommerceMembers.cart.unnamedProduct'),
                image: productData.imageUrl || (productData.images && productData.images.split(',')[0]) || item.image || '',
                price: productData.salePrice !== undefined ? productData.salePrice : 0,
              };
            } else {
              return { ...item, name: t('ecommerceMembers.cart.unknownProductOutOfStock'), image: item.image || '', price: 0 };
            }
          } catch (productError) {
            console.error(`[handleViewCart] Ürün detayı alınırken hata (${item.productId}):`, productError);
            return { ...item, name: t('ecommerceMembers.cart.unknownProductDataUnavailable'), image: item.image || '', price: 0 };
          }
        });

        const enrichedCartList = await Promise.all(enrichedCartPromises);
        setMemberCart(enrichedCartList);
      } else {
        setMemberCart([]);
      }
    } catch (err) {
      console.error("[handleViewCart] Sepet verileri alınırken genel hata:", err);
      setMemberCart([]);
    } finally {
      setLoadingCart(false);
    }
  };

  // Filtrelenmiş üyeler
  const filteredMembers = members.filter(member => {
    const matchesSearch = !searchTerm || 
      member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.phone?.includes(searchTerm);

    const matchesFilter = filterStatus === 'all' || 
      (filterStatus === 'active' && member.isActive && !member.isBlocked) ||
      (filterStatus === 'blocked' && member.isBlocked);

    return matchesSearch && matchesFilter;
  });

  // Sayfalama
  const paginatedMembers = filteredMembers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Üye durumunu güncelle
  const updateMemberStatus = async (memberId: string, field: 'isActive' | 'isBlocked', value: boolean) => {
    if (!companyId) return;

    try {
      const memberRef = ref(database, `companies/${companyId}/ecommerce/members/${memberId}`);
      await update(memberRef, { [field]: value });
    } catch (error) {
      console.error('Üye durumu güncellenirken hata:', error);
      setError(t('ecommerceMembers.errors.updateStatusFailed'));
    }
  };

  // Üye sil
  const deleteMember = async () => {
    if (!companyId || !selectedMember) return;

    try {
      const memberRef = ref(database, `companies/${companyId}/ecommerce/members/${selectedMember.id}`);
      await remove(memberRef);
      setDeleteConfirmOpen(false);
      setSelectedMember(null);
    } catch (error) {
      console.error('Üye silinirken hata:', error);
      setError(t('ecommerceMembers.errors.deleteMemberFailed'));
    }
  };

  // Tarih formatlama
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Para formatlama
  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount);
  };

  // ===== B2B üye→cari eşleştirme işlemleri (Faz 5.3) =====
  const openAssignDialog = async (member: Member) => {
    setAssignMember(member);
    setAssignIsB2B(member.isB2B === true);
    setAssignIsB2BApproved(member.isB2BApproved === true);
    setAssignSelectedAccount(null);
    setAssignOpen(true);
    setAssignAccountsLoading(true);
    try {
      const res = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
      const list = res.data?.data || res.data || [];
      setAssignAccounts(Array.isArray(list) ? list : []);
      if (member.accountId) {
        const current = (Array.isArray(list) ? list : []).find((a: any) => a.id === member.accountId);
        setAssignSelectedAccount(current || null);
      }
    } catch (err) {
      console.error('[MembersManagement] Cari listesi yüklenemedi:', err);
      setSnackbar({ open: true, message: t('ecommerceMembers.assign.accountsLoadError'), severity: 'error' });
    } finally {
      setAssignAccountsLoading(false);
    }
  };

  const handleAssignSave = async (unlink = false) => {
    if (!assignMember) return;
    setAssignLoading(true);
    try {
      const payload = {
        memberId: assignMember.id,
        accountId: unlink ? null : (assignSelectedAccount?.id ?? null),
        isB2B: unlink ? false : assignIsB2B,
        isB2BApproved: unlink ? false : assignIsB2BApproved
      };
      const res = await ecommerceApi.post('/api/ecommerce/members/assign-account', payload);
      if (res.data?.success) {
        const d = res.data.data;
        setMembers(prev => prev.map(m => m.id === assignMember.id
          ? { ...m, accountId: d.accountId, accountName: d.accountName ?? null, isB2B: d.isB2B, isB2BApproved: d.isB2BApproved }
          : m));
        setSnackbar({ open: true, message: res.data.message || t('ecommerceMembers.assign.saved'), severity: 'success' });
        setAssignOpen(false);
      } else {
        throw new Error(res.data?.message);
      }
    } catch (err: any) {
      console.error('[MembersManagement] Üye→cari eşleştirme hatası:', err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || t('ecommerceMembers.assign.saveError'),
        severity: 'error'
      });
    } finally {
      setAssignLoading(false);
    }
  };

  const renderB2BChip = (member: Member) => {
    if (!member.isB2B) {
      return <Chip size="small" label="B2C" variant="outlined" />;
    }
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Chip
          size="small"
          label={member.isB2BApproved ? t('ecommerceMembers.assign.b2bApproved') : t('ecommerceMembers.assign.b2bPending')}
          color={member.isB2BApproved ? 'success' : 'warning'}
        />
        {member.accountName && (
          <Tooltip title={t('ecommerceMembers.assign.linkedAccount')}>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BusinessIcon sx={{ fontSize: '0.9rem' }} /> {member.accountName}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  };

  const renderSellerApplicationChip = (member: Member) => {
    const status = member.sellerApplicationStatus || 'none';
    if (status === 'pending') {
      return <Chip size="small" label={t('ecommerceMembers.sellerApplication.done')} color="warning" />;
    }
    if (status === 'approved') {
      return <Chip size="small" label={t('ecommerceMembers.sellerApplication.approved')} color="success" />;
    }
    if (status === 'rejected') {
      return <Chip size="small" label={t('ecommerceMembers.sellerApplication.rejected')} color="error" />;
    }
    return <Chip size="small" label={t('ecommerceMembers.sellerApplication.none')} variant="outlined" />;
  };

  if (!companyId) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="warning">
          {t('ecommerceMembers.errors.companyInfoRequired')}
        </Alert>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <GroupIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            {t('ecommerceMembers.title')}
          </Typography>
          <GuideButton guideKey="ecommerce.members" sx={{ ml: 0.5 }} />
        </Box>

        {/* İstatistik Kartları */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="overline">
                      {t('ecommerceMembers.stats.totalMembers')}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {stats.totalMembers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                    <GroupIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="overline">
                      {t('ecommerceMembers.stats.activeMembers')}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {stats.activeMembers}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                    <CheckCircleIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="overline">
                      {t('ecommerceMembers.stats.newThisMonth')}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {stats.newMembersThisMonth}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                    <TrendingUpIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="overline">
                      {t('ecommerceMembers.stats.totalRevenue')}
                    </Typography>
                    <Typography variant="h4" component="div">
                      {formatMoney(stats.totalRevenue)}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                    <AttachMoneyIcon />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {partnerLoadWarning && <Alert severity="warning" sx={{ mb: 2 }}>{partnerLoadWarning}</Alert>}

        {/* Filtreler ve Arama */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder={t('ecommerceMembers.filters.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('ecommerceMembers.filters.statusFilter')}</InputLabel>
                  <Select
                    value={filterStatus}
                    label={t('ecommerceMembers.filters.statusFilter')}
                    onChange={(e) => setFilterStatus(e.target.value as any)}
                  >
                    <MenuItem value="all">{t('ecommerceMembers.filters.all')}</MenuItem>
                    <MenuItem value="active">{t('ecommerceMembers.filters.active')}</MenuItem>
                    <MenuItem value="blocked">{t('ecommerceMembers.filters.blocked')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<GetAppIcon />}
                    onClick={() => {
                      // CSV export fonksiyonu eklenebilir
                      console.log('CSV export');
                    }}
                  >
                    {t('ecommerceMembers.actions.export')}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => {
                      // Yeni üye ekleme modal'ı açılabilir
                      console.log('Yeni üye ekle');
                    }}
                  >
                    {t('ecommerceMembers.actions.newMember')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Üye Listesi */}
        <Card>
          <CardContent>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : filteredMembers.length === 0 ? (
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <Typography variant="h6" color="textSecondary">
                  {searchTerm || filterStatus !== 'all' ? t('ecommerceMembers.empty.filtered') : t('ecommerceMembers.empty.noMembers')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('ecommerceMembers.empty.description')}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('ecommerceMembers.table.member')}</TableCell>
                        <TableCell>{t('ecommerceMembers.table.contact')}</TableCell>
                        <TableCell>{t('ecommerceMembers.table.status')}</TableCell>
                        <TableCell>{t('ecommerceMembers.table.b2b')}</TableCell>
                        <TableCell>{t('ecommerceMembers.table.sellerApplication')}</TableCell>
                        <TableCell>{t('ecommerceMembers.table.registerDate')}</TableCell>
                        <TableCell>{t('ecommerceMembers.table.order')}</TableCell>
                        <TableCell>{t('ecommerceMembers.table.spending')}</TableCell>
                        <TableCell align="center">{t('ecommerceMembers.table.cart')}</TableCell>
                        <TableCell align="center">{t('ecommerceMembers.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedMembers.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar 
                                src={member.avatar} 
                                sx={{ mr: 2 }}
                              >
                                {member.displayName?.[0] || member.firstName?.[0] || member.email?.[0]?.toUpperCase() || 'U'}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2">
                                  {member.displayName || `${member.firstName} ${member.lastName}`.trim() || t('ecommerceMembers.table.unnamed')}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  ID: {member.id.substring(0, 8)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{member.email}</Typography>
                            {member.phone && (
                              <Typography variant="caption" color="textSecondary">
                                {member.phone}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Chip
                                size="small"
                                label={member.isActive ? t('ecommerceMembers.status.active') : t('ecommerceMembers.status.inactive')}
                                color={member.isActive ? 'success' : 'default'}
                              />
                              {member.isBlocked && (
                                <Chip
                                  size="small"
                                  label={t('ecommerceMembers.status.blocked')}
                                  color="error"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {renderB2BChip(member)}
                          </TableCell>
                          <TableCell>
                            {renderSellerApplicationChip(member)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(member.createdAt)}
                            </Typography>
                            {member.lastLoginAt && (
                              <Typography variant="caption" color="textSecondary">
                                {t('ecommerceMembers.table.last')}: {formatDate(member.lastLoginAt)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {t('ecommerceMembers.table.orderCount', { count: member.orderCount })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatMoney(member.totalSpent)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {member.cartItemCount > 0 && (
                              <Tooltip title={t('ecommerceMembers.table.cartTooltip', { count: member.cartItemCount })}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <ShoppingCartIcon fontSize="small" color="primary" />
                                  <InventoryIcon fontSize="small" color="action" />
                                  <Typography variant="caption" color="textSecondary">
                                    ({member.cartItemCount})
                                  </Typography>
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              aria-label={t('ecommerceMembers.table.actions')}
                              onClick={(event) => handleMenuOpen(event, member)}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  component="div"
                  count={filteredMembers.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                  labelRowsPerPage={t('ecommerceMembers.pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} / ${count !== -1 ? count : `${to}'den fazla`}`
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          // Delay clearing to allow actions to fire
          setTimeout(() => setCurrentMemberMenu(null), 200);
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={() => {
            if (currentMemberMenu) {
                setSelectedMember(currentMemberMenu);
                setDetailsOpen(true);
            }
            handleMenuClose();
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('ecommerceMembers.menu.viewDetails')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
            if (currentMemberMenu) {
                handleViewCart(currentMemberMenu);
            }
            handleMenuClose();
        }}>
          <ListItemIcon>
            <ShoppingCartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('ecommerceMembers.menu.viewCart')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
            if (currentMemberMenu) {
                openAssignDialog(currentMemberMenu);
            }
            handleMenuClose();
        }}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('ecommerceMembers.menu.assignAccount')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
            if (currentMemberMenu) {
                updateMemberStatus(currentMemberMenu.id, 'isBlocked', !currentMemberMenu.isBlocked);
            }
            handleMenuClose();
        }}>
          <ListItemIcon>
            {currentMemberMenu?.isBlocked ? <CheckCircleIcon fontSize="small" color="success"/> : <BlockIcon fontSize="small" color="error"/>}
          </ListItemIcon>
          <ListItemText>{currentMemberMenu?.isBlocked ? t('ecommerceMembers.menu.unblock') : t('ecommerceMembers.menu.block')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => {
            if (currentMemberMenu) {
                setSelectedMember(currentMemberMenu);
                setDeleteConfirmOpen(true);
            }
            handleMenuClose();
        }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error"/>
          </ListItemIcon>
          <ListItemText>{t('ecommerceMembers.menu.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Üye Detayları Modal */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedMember && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar src={selectedMember.avatar} sx={{ mr: 2 }}>
                  {selectedMember.displayName?.[0] || selectedMember.email?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6">
                    {selectedMember.displayName || `${selectedMember.firstName} ${selectedMember.lastName}`.trim() || t('ecommerceMembers.table.unnamed')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {selectedMember.email}
                  </Typography>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>{t('ecommerceMembers.details.personalInfo')}</Typography>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>{t('ecommerceMembers.details.email')}:</strong> {selectedMember.email}</Typography>
                    <Typography variant="body2"><strong>{t('ecommerceMembers.details.phone')}:</strong> {selectedMember.phone || t('ecommerceMembers.details.unspecified')}</Typography>
                    <Typography variant="body2"><strong>{t('ecommerceMembers.details.registerDate')}:</strong> {formatDate(selectedMember.createdAt)}</Typography>
                    {selectedMember.lastLoginAt && (
                      <Typography variant="body2"><strong>{t('ecommerceMembers.details.lastLogin')}:</strong> {formatDate(selectedMember.lastLoginAt)}</Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>{t('ecommerceMembers.details.shoppingStats')}</Typography>
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2"><strong>{t('ecommerceMembers.details.totalOrders')}:</strong> {selectedMember.orderCount}</Typography>
                    <Typography variant="body2"><strong>{t('ecommerceMembers.details.totalSpending')}:</strong> {formatMoney(selectedMember.totalSpent)}</Typography>
                    <Typography variant="body2"><strong>{t('ecommerceMembers.details.averageCart')}:</strong> {formatMoney(selectedMember.orderCount > 0 ? selectedMember.totalSpent / selectedMember.orderCount : 0)}</Typography>
                  </Box>
                </Grid>
                {selectedMember.addresses && selectedMember.addresses.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>{t('ecommerceMembers.details.addresses')}</Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      {selectedMember.addresses.map((address, index) => (
                        <Box key={address.id} sx={{ mb: index < selectedMember.addresses!.length - 1 ? 2 : 0 }}>
                          <Typography variant="body2">
                            <strong>{address.title}{address.isDefault ? ` (${t('ecommerceMembers.details.default')})` : ''}</strong>
                          </Typography>
                          <Typography variant="caption">
                            {address.fullName}, {address.address}, {address.district}/{address.city} {address.postalCode}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailsOpen(false)}>{t('ecommerceMembers.actions.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Silme Onayı Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{t('ecommerceMembers.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('ecommerceMembers.deleteDialog.description', { email: selectedMember?.email || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>{t('ecommerceMembers.actions.cancel')}</Button>
          <Button onClick={deleteMember} color="error" variant="contained">
            {t('ecommerceMembers.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Üye Sepeti Modal */}
      <Dialog
        open={cartModalOpen}
        onClose={() => setCartModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMember && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ShoppingCartIcon sx={{ mr: 1.5, color: 'primary.main' }} />
              <Typography variant="h6">{t('ecommerceMembers.cart.title', { name: selectedMember.displayName || selectedMember.email })}</Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {loadingCart ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : memberCart.length === 0 ? (
            <Typography sx={{ p: 4, textAlign: 'center' }}>{t('ecommerceMembers.cart.empty')}</Typography>
          ) : (
            <List>
              {memberCart.map((item: any) => (
                <ListItem key={item.id} divider>
                  <ListItemIcon sx={{ mr: 2 }}>
                    <Avatar 
                      variant="rounded" 
                      src={item.image} 
                      alt={item.name} 
                      sx={{ width: 64, height: 64 }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    secondary={
                      <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                        <Typography component="span" variant="body2" color="text.primary">
                          {t('ecommerceMembers.cart.itemQuantity', { quantity: item.quantity })} x {formatMoney(item.price)}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                          {t('ecommerceMembers.cart.total')}: <strong>{formatMoney(item.quantity * item.price)}</strong>
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCartModalOpen(false)}>{t('ecommerceMembers.actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Üye→Cari Eşleştirme Modal (Faz 5.3) */}
      <Dialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LinkIcon sx={{ mr: 1.5, color: 'primary.main' }} />
            <Typography variant="h6">
              {t('ecommerceMembers.assign.title', { name: assignMember?.displayName || assignMember?.email || '' })}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {assignAccountsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={assignAccounts}
                  loading={assignAccountsLoading}
                  getOptionLabel={(option: any) =>
                    `${option.companyName || ''}${option.accountCode ? ` (${option.accountCode})` : ''}`}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                  value={assignSelectedAccount}
                  onChange={(_, value) => setAssignSelectedAccount(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('ecommerceMembers.assign.accountLabel')}
                      placeholder={t('ecommerceMembers.assign.accountPlaceholder')}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={assignIsB2B}
                      onChange={(e) => {
                        setAssignIsB2B(e.target.checked);
                        if (!e.target.checked) setAssignIsB2BApproved(false);
                      }}
                    />
                  }
                  label={t('ecommerceMembers.assign.isB2B')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={assignIsB2BApproved}
                      onChange={(e) => setAssignIsB2BApproved(e.target.checked)}
                      disabled={!assignIsB2B}
                    />
                  }
                  label={t('ecommerceMembers.assign.isB2BApproved')}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  {t('ecommerceMembers.assign.hintShort', 'Üye bir cariye bağlandığında faturalar bu cariye kesilir.')}
                </Alert>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {assignMember?.accountId && (
            <Button
              color="error"
              onClick={() => handleAssignSave(true)}
              disabled={assignLoading}
            >
              {t('ecommerceMembers.assign.unlink')}
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setAssignOpen(false)} disabled={assignLoading}>
            {t('ecommerceMembers.actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleAssignSave(false)}
            disabled={assignLoading || !assignSelectedAccount}
            startIcon={assignLoading ? <CircularProgress size={18} color="inherit" /> : <LinkIcon />}
          >
            {t('ecommerceMembers.assign.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default MembersManagement;
